import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatDate, formatDateLong, formatCurrency } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Users, Plus, Search, Pencil, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Upload, Download, CheckCircle2, FileText, Receipt, MessageSquare, Phone, Mail, Calendar, Tag } from "lucide-react";
import { toast } from "sonner";
import { validateEmail } from "@/lib/validation";
import CSVImportDialog from "@/components/CSVImportDialog";

const emptyForm = {
  company_name: "", contact_person: "", email: "", phone: "",
  address: "", delivery_address: "", payment_terms: "", tax_vat_number: "",
  credit_limit: 0, status: "Active", customer_type: "Retail", notes: "",
  default_price_list_id: "",
};

export default function CustomersPage() {
  const { id } = useParams();
  if (id) return <CustomerDetail customerId={id} />;
  return <CustomerList />;
}

function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeCount, setActiveCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const canEdit = ["admin", "sales_manager"].includes(user?.role);
  const canCreate = ["admin", "sales_manager", "sales_rep"].includes(user?.role);
  const canDelete = ["admin", "sales_manager"].includes(user?.role);

  const handleExport = async () => {
    try {
      const res = await api.get("/export/customers", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "customers_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Customers exported successfully");
    } catch { toast.error("Failed to export customers"); }
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, status: statusFilter, customer_type: typeFilter, page, limit: 25 };
      const res = await api.get("/customers", { params });
      setCustomers(res.data.customers);
      setTotalPages(res.data.pages);
      setActiveCount(res.data.active_count);
    } catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  }, [search, statusFilter, typeFilter, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    if (searchParams.get("add") === "true" && canCreate) {
      setForm({ ...emptyForm }); setEditCustomer(null); setDialogOpen(true);
    }
  }, [searchParams, canCreate]);

  const handleSave = async () => {
    if (!form.company_name) { toast.error("Company name is required"); return; }
    if (!form.email) { toast.error("Email is required"); return; }
    const emailCheck = validateEmail(form.email);
    if (!emailCheck.valid) { toast.error(emailCheck.error); return; }
    setSaving(true);
    try {
      if (editCustomer) {
        await api.put(`/customers/${editCustomer.customer_id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      setDialogOpen(false); fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/customers/${deleteId}`);
      toast.success("Customer deleted"); setDeleteId(null); fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const openEdit = (c) => { setEditCustomer(c); setForm({ ...emptyForm, ...c }); setDialogOpen(true); };
  const openAdd = () => { setEditCustomer(null); setForm({ ...emptyForm }); setDialogOpen(true); };

  const typeBadge = (type) => type === "Wholesale"
    ? "bg-orange-50 text-orange-700 border-orange-200"
    : "bg-blue-50 text-blue-700 border-blue-200";

  return (
    <div className="animate-fade-in" data-testid="customers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Customers</h1>
          <div className="text-sm text-gray-500 mt-1">
            <Badge className="bg-green-50 text-green-700 border-green-200 rounded-full text-xs mr-2">{activeCount} Active</Badge>
            Manage your customer relationships
          </div>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <Button data-testid="export-customers-button" variant="outline" onClick={handleExport} className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button data-testid="import-customers-button" variant="outline" onClick={() => setImportOpen(true)} className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50">
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
            <Button data-testid="add-customer-button" onClick={openAdd} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Add New Customer
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="customer-search-input" placeholder="Search by company or contact..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 h-9 rounded-sm border-gray-300" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="customer-status-filter" className="w-[150px] h-9 rounded-sm border-gray-300"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="customer-type-filter" className="w-[150px] h-9 rounded-sm border-gray-300"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Retail">Retail</SelectItem>
            <SelectItem value="Wholesale">Wholesale</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-100"><td colSpan={7} className="px-4 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td></tr>
                ))
              ) : customers.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                  <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No customers found</p>
                </td></tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.customer_id} data-testid={`customer-row-${c.customer_id}`}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/customers/${c.customer_id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-[#1F2937]">{c.company_name}</td>
                    <td className="px-4 py-3 text-gray-600">{c.contact_person || "-"}</td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{c.email}</td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{c.phone || "-"}</td>
                    <td className="px-4 py-3"><Badge className={`rounded-full text-xs ${typeBadge(c.customer_type)}`}>{c.customer_type}</Badge></td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-full text-xs ${c.status === "Active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>{c.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && <Button data-testid={`edit-customer-${c.customer_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => openEdit(c)}><Pencil className="w-4 h-4" /></Button>}
                        {canDelete && <Button data-testid={`delete-customer-${c.customer_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(c.customer_id)}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-8 rounded-sm"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-8 rounded-sm"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">{editCustomer ? "Edit Customer" : "Add New Customer"}</DialogTitle></DialogHeader>
          <CustomerForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-customer-button" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {saving ? "Saving..." : "Save Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Are you sure?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this customer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer" onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CSVImportDialog open={importOpen} onOpenChange={setImportOpen} entityType="customers" onComplete={fetchCustomers} />
    </div>
  );
}

function CustomerForm({ form, setForm }) {
  const [emailErr, setEmailErr] = useState(null);
  const [emailOk, setEmailOk] = useState(false);
  const [priceLists, setPriceLists] = useState([]);
  const [plInfo, setPlInfo] = useState(null);
  const f = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    api.get("/price-lists?status=Active&limit=100").then(r => setPriceLists(r.data.price_lists || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.default_price_list_id) {
      api.get(`/price-lists/${form.default_price_list_id}/info`).then(r => setPlInfo(r.data)).catch(() => setPlInfo(null));
    } else {
      setPlInfo(null);
    }
  }, [form.default_price_list_id]);

  const checkEmail = () => {
    if (!form.email) { setEmailErr(null); setEmailOk(false); return; }
    const r = validateEmail(form.email);
    setEmailErr(r.valid ? null : r.error);
    setEmailOk(r.valid);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Company Name <span className="text-[#E63946]">*</span></label>
        <Input data-testid="customer-company-name" value={form.company_name} onChange={f("company_name")} placeholder="Enter company name" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Contact Person</label>
        <Input data-testid="customer-contact-person" value={form.contact_person} onChange={f("contact_person")} placeholder="Contact name" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Email <span className="text-[#E63946]">*</span></label>
        <div className="relative">
          <Input data-testid="customer-email" type="email" value={form.email} onChange={f("email")} onBlur={checkEmail}
            placeholder="email@company.com" className={`rounded-sm h-10 pr-8 ${emailErr ? "border-[#E63946]" : "border-gray-300"}`} />
          {emailOk && <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#10B981]" />}
        </div>
        {emailErr && <p data-testid="customer-email-error" className="text-xs text-[#E63946] mt-1">{emailErr}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Phone</label>
        <Input data-testid="customer-phone" value={form.phone} onChange={f("phone")} placeholder="(012) 345-6789" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-[#374151] mb-1">Physical Address</label>
        <Input data-testid="customer-address" value={form.address} onChange={f("address")} placeholder="Physical address" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-[#374151] mb-1">Delivery Address</label>
        <Input data-testid="customer-delivery-address" value={form.delivery_address} onChange={f("delivery_address")} placeholder="Delivery address (if different)" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Customer Type</label>
        <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
          <SelectTrigger data-testid="customer-type-select" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Retail">Retail</SelectItem>
            <SelectItem value="Wholesale">Wholesale</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Payment Terms</label>
        <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
          <SelectTrigger className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select terms" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Cash on delivery">Cash on delivery</SelectItem>
            <SelectItem value="30 days">30 days</SelectItem>
            <SelectItem value="60 days">60 days</SelectItem>
            <SelectItem value="90 days">90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Tax/VAT Number</label>
        <Input value={form.tax_vat_number} onChange={f("tax_vat_number")} placeholder="VAT number" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Credit Limit (ZAR)</label>
        <Input data-testid="customer-credit-limit" type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })} placeholder="0.00" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Status</label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger data-testid="customer-status-select" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pricing & Billing Section */}
      <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
        <h3 className="text-sm font-semibold text-[#1F2937] mb-3 flex items-center gap-1.5">
          <Tag className="w-4 h-4 text-[#E63946]" />Pricing & Billing
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1">Default Price List</label>
            <Select value={form.default_price_list_id || "none"} onValueChange={(v) => setForm({ ...form, default_price_list_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="customer-price-list-select" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Use System Default" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use System Default</SelectItem>
                {priceLists.map(pl => (
                  <SelectItem key={pl.pl_id} value={pl.pl_id}>
                    {pl.name}{pl.is_default ? " (System Default)" : ""} — {pl.type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-400 mt-1">Auto-applied when creating quotes and invoices for this customer</p>
          </div>
          {plInfo && (
            <div className="bg-gray-50 rounded-sm border border-gray-200 p-3" data-testid="price-list-info-card">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Selected Price List</p>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-500">Type:</span> <span className="font-medium">{plInfo.type}</span></p>
                <p><span className="text-gray-500">Products:</span> <span className="font-medium">{plInfo.product_count} items</span></p>
                <p><span className="text-gray-500">Customers:</span> <span className="font-medium">{plInfo.customer_count} assigned</span></p>
                {plInfo.description && <p className="text-xs text-gray-400 mt-1">{plInfo.description}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-[#374151] mb-1">Notes</label>
        <textarea data-testid="customer-notes" value={form.notes} onChange={f("notes")} placeholder="Additional notes..."
          className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" />
      </div>
      {/* VAT Information */}
      <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
        <p className="text-sm font-semibold text-[#1F2937] mb-3">VAT Information</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          <label className="flex items-center gap-2 cursor-pointer md:col-span-2">
            <input data-testid="customer-vat-registered" type="checkbox" checked={form.vat_registered || false}
              onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })}
              className="rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]" />
            <span className="text-sm font-medium text-[#374151]">Customer is VAT registered</span>
          </label>
          {form.vat_registered && (
            <>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Customer VAT Number</label>
                <Input data-testid="customer-vat-number" value={form.vat_number || ""} onChange={f("vat_number")} placeholder="4111222333" maxLength={10} className="rounded-sm border-gray-300 h-9" />
                <p className="text-[10px] text-gray-400 mt-0.5">Required for tax invoices. 10 digits starting with 4</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Tax Type</label>
                <select data-testid="customer-tax-type" value={form.tax_type || "customer"} onChange={(e) => setForm({ ...form, tax_type: e.target.value })}
                  className="w-full rounded-sm border border-gray-300 px-3 py-1.5 text-sm h-9 focus:outline-none focus:ring-2 focus:ring-[#E63946]">
                  <option value="customer">Standard Customer (South Africa)</option>
                  <option value="export">Export Customer (Zero-Rated)</option>
                </select>
              </div>
            </>
          )}
          {!form.vat_registered && (
            <p className="text-xs text-gray-500 md:col-span-2">Non-VAT registered: Invoices will still show VAT breakdown but won't include customer VAT number.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function CustomerDetail({ customerId }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("details");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [comms, setComms] = useState([]);
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [editComm, setEditComm] = useState(null);
  const [deleteCommId, setDeleteCommId] = useState(null);
  const [plInfo, setPlInfo] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["admin", "sales_manager"].includes(user?.role);

  useEffect(() => {
    api.get(`/customers/${customerId}`).then((r) => {
      setCustomer(r.data); setForm({ ...emptyForm, ...r.data });
    }).catch(() => toast.error("Customer not found")).finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    if (customer?.default_price_list_id) {
      api.get(`/price-lists/${customer.default_price_list_id}/info`).then(r => setPlInfo(r.data)).catch(() => setPlInfo(null));
    } else {
      setPlInfo(null);
    }
  }, [customer]);

  useEffect(() => {
    if (customer) {
      api.get(`/customers/${customerId}/stats`).then((r) => setStats(r.data)).catch(() => {});
      api.get("/quotes", { params: { customer_id: customerId, limit: 100 } }).then((r) => setQuotes(r.data.quotes || [])).catch(() => {});
      api.get("/invoices", { params: { customer_id: customerId, limit: 100 } }).then((r) => setInvoices(r.data.invoices || [])).catch(() => {});
      api.get("/communications", { params: { entity_type: "customer", entity_id: customerId, limit: 100 } }).then((r) => setComms(r.data.communications || [])).catch(() => {});
    }
  }, [customer, customerId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/customers/${customerId}`, form);
      setCustomer(res.data); setEditOpen(false); toast.success("Customer updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDeleteComm = async () => {
    try { await api.delete(`/communications/${deleteCommId}`); toast.success("Communication deleted"); setDeleteCommId(null);
      api.get("/communications", { params: { entity_type: "customer", entity_id: customerId, limit: 100 } }).then((r) => setComms(r.data.communications || []));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!customer) return <p className="text-gray-400">Customer not found</p>;

  const fields = [
    ["Company Name", customer.company_name], ["Contact Person", customer.contact_person],
    ["Email", customer.email], ["Phone", customer.phone],
    ["Address", customer.address], ["Delivery Address", customer.delivery_address],
    ["Customer Type", customer.customer_type], ["Payment Terms", customer.payment_terms],
    ["Tax/VAT Number", customer.tax_vat_number], ["Credit Limit", formatCurrency(customer.credit_limit)],
    ["Date Added", formatDateLong(customer.created_at)], ["Last Updated", formatDateLong(customer.updated_at)],
  ];

  const tabs = [
    { id: "details", label: "Details" },
    { id: "quotes", label: "Quotes", count: quotes.length },
    { id: "invoices", label: "Invoices", count: invoices.length },
    { id: "communications", label: "Communications", count: comms.length },
  ];

  const qs = stats?.quote_stats;
  const is_ = stats?.invoice_stats;
  const aging = stats?.aging;

  const statusColors = {
    Draft: "bg-gray-100 text-gray-700", Sent: "bg-blue-50 text-blue-700", Accepted: "bg-green-50 text-green-700",
    Rejected: "bg-red-50 text-red-700", Expired: "bg-yellow-50 text-yellow-700", Converted: "bg-purple-50 text-purple-700",
    "Partially Paid": "bg-orange-50 text-orange-700", Paid: "bg-green-50 text-green-700",
    Overdue: "bg-red-50 text-red-700", Void: "bg-gray-100 text-gray-400",
  };

  const commTypeIcons = { Email: Mail, "Phone Call": Phone, "Meeting / In Person": Users, Other: MessageSquare };

  return (
    <div className="animate-fade-in" data-testid="customer-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/customers")} className="h-8 w-8"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{customer.company_name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`rounded-full text-xs ${customer.status === "Active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>{customer.status}</Badge>
            <Badge className={`rounded-full text-xs ${customer.customer_type === "Wholesale" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{customer.customer_type}</Badge>
            {is_?.overdue > 0 && <Badge className="bg-red-50 text-red-700 rounded-full text-xs">Overdue: {formatCurrency(is_.overdue)}</Badge>}
            {is_?.outstanding > 0 && <Badge className="bg-orange-50 text-orange-700 rounded-full text-xs">Balance: {formatCurrency(is_.outstanding)}</Badge>}
          </div>
        </div>
        {canEdit && (
          <Button data-testid="edit-customer-detail" onClick={() => setEditOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            <Pencil className="w-4 h-4 mr-1.5" /> Edit
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`customer-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === t.id ? "border-[#E63946] text-[#E63946]" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
            {t.label} {t.count != null && <span className="ml-1 text-xs text-gray-400">({t.count})</span>}
          </button>
        ))}
      </div>

      {/* DETAILS TAB */}
      {tab === "details" && (
        <div className="space-y-4">
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fields.map(([label, value]) => (
                  <div key={label}><p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{label}</p><p className="text-sm text-[#1F2937]">{value || "-"}</p></div>
                ))}
              </div>
              {customer.notes && <div className="mt-6 pt-6 border-t border-gray-100"><p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Notes</p><p className="text-sm text-[#1F2937] whitespace-pre-wrap">{customer.notes}</p></div>}
            </CardContent>
          </Card>

          {/* Pricing & Billing Card */}
          <Card className="border border-gray-200 rounded-sm shadow-sm" data-testid="customer-pricing-card">
            <CardContent className="p-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" />Pricing & Billing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Default Price List</p>
                  {plInfo ? (
                    <div>
                      <button onClick={() => navigate(`/price-lists/${plInfo.pl_id}`)} className="text-sm font-medium text-[#E63946] hover:underline" data-testid="customer-price-list-link">
                        {plInfo.name}
                      </button>
                      <span className="text-xs text-gray-400 ml-2">({plInfo.type})</span>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Using system default</p>
                  )}
                </div>
                {plInfo && (
                  <>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Products in Price List</p>
                      <p className="text-sm text-[#1F2937]">{plInfo.product_count} items</p>
                    </div>
                    {plInfo.description && (
                      <div className="md:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Description</p>
                        <p className="text-sm text-[#1F2937]">{plInfo.description}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* QUOTES TAB */}
      {tab === "quotes" && (
        <div className="space-y-4">
          {qs && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Quotes</p><p className="font-heading font-bold text-xl">{qs.total}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Value</p><p className="font-heading font-bold text-xl">{formatCurrency(qs.total_value)}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Accepted</p><p className="font-heading font-bold text-xl text-green-600">{qs.accepted}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Pending</p><p className="font-heading font-bold text-xl text-blue-600">{qs.sent}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Conversion</p><p className="font-heading font-bold text-xl text-purple-600">{qs.conversion_rate}%</p></CardContent></Card>
            </div>
          )}
          <div className="flex justify-end">
            <Button data-testid="create-quote-from-customer" size="sm" onClick={() => navigate(`/quotes?customer=${customerId}`)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-3.5 h-3.5 mr-1" /> New Quote</Button>
          </div>
          <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Quote #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Valid Until</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr></thead>
              <tbody>
                {quotes.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No quotes for this customer</td></tr>
                ) : quotes.map((q) => (
                  <tr key={q.quote_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/quotes/${q.quote_id}`)}>
                    <td className="px-4 py-3 font-mono font-medium text-[#E63946]">{q.quote_number}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDateLong(q.created_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{q.valid_until || "-"}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(q.total)}</td>
                    <td className="px-4 py-3"><Badge className={`rounded-full text-xs ${statusColors[q.status] || ""}`}>{q.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* INVOICES TAB */}
      {tab === "invoices" && (
        <div className="space-y-4">
          {is_ && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Invoiced</p><p className="font-heading font-bold text-xl">{formatCurrency(is_.total_invoiced)}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Paid</p><p className="font-heading font-bold text-xl text-green-600">{formatCurrency(is_.total_paid)}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Outstanding</p><p className="font-heading font-bold text-xl text-orange-600">{formatCurrency(is_.outstanding)}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Overdue</p><p className={`font-heading font-bold text-xl ${is_.overdue > 0 ? "text-[#E63946]" : "text-gray-400"}`}>{formatCurrency(is_.overdue)}</p></CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Invoices</p><p className="font-heading font-bold text-xl">{is_.total}</p></CardContent></Card>
            </div>
          )}
          {aging && (is_.outstanding > 0) && (
            <Card className="border border-gray-200 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Aging Report</p>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-green-50 rounded-sm"><p className="text-xs text-green-700 font-medium">Current (0-30)</p><p className="font-bold text-green-700">{formatCurrency(aging.current)}</p></div>
                  <div className="text-center p-3 bg-yellow-50 rounded-sm"><p className="text-xs text-yellow-700 font-medium">31-60 days</p><p className="font-bold text-yellow-700">{formatCurrency(aging.days_31_60)}</p></div>
                  <div className="text-center p-3 bg-orange-50 rounded-sm"><p className="text-xs text-orange-700 font-medium">61-90 days</p><p className="font-bold text-orange-700">{formatCurrency(aging.days_61_90)}</p></div>
                  <div className={`text-center p-3 rounded-sm ${aging.days_90_plus > 0 ? "bg-red-50" : "bg-gray-50"}`}><p className={`text-xs font-medium ${aging.days_90_plus > 0 ? "text-red-700" : "text-gray-500"}`}>90+ days</p><p className={`font-bold ${aging.days_90_plus > 0 ? "text-red-700" : "text-gray-400"}`}>{formatCurrency(aging.days_90_plus)}</p></div>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex justify-end">
            <Button data-testid="create-invoice-from-customer" size="sm" onClick={() => navigate(`/invoices?customer=${customerId}`)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-3.5 h-3.5 mr-1" /> New Invoice</Button>
          </div>
          <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Due Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Paid</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Balance</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr></thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-400 text-sm">No invoices for this customer</td></tr>
                ) : invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
                    <td className="px-4 py-3 font-mono font-medium text-[#E63946]">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDateLong(inv.created_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{inv.due_date || "-"}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(inv.total)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{formatCurrency(inv.amount_paid)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(inv.balance)}</td>
                    <td className="px-4 py-3"><Badge className={`rounded-full text-xs ${statusColors[inv.status] || ""}`}>{inv.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* COMMUNICATIONS TAB */}
      {tab === "communications" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button data-testid="add-communication-button" size="sm" onClick={() => { setEditComm(null); setCommDialogOpen(true); }} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-3.5 h-3.5 mr-1" /> Add Communication</Button>
          </div>
          {comms.length === 0 ? (
            <Card className="border border-gray-200 rounded-sm shadow-sm">
              <CardContent className="p-8 text-center text-gray-400">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No communications logged</p>
                <p className="text-xs mt-1">Log your interactions with this customer</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {comms.map((c) => {
                const Icon = commTypeIcons[c.comm_type] || MessageSquare;
                return (
                  <Card key={c.comm_id} className="border border-gray-200 rounded-sm shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-0.5"><Icon className="w-4 h-4 text-gray-500" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-[#1F2937]">{c.subject}</span>
                              <Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{c.comm_type || c.type}</Badge>
                              {c.auto_logged && <Badge data-testid={`auto-logged-${c.comm_id}`} className="rounded-full text-[10px] bg-blue-50 text-blue-600 border border-blue-200">Auto-logged</Badge>}
                              {c.follow_up_required && <Badge className="rounded-full text-xs bg-orange-50 text-orange-700">Follow-up: {c.follow_up_date}</Badge>}
                            </div>
                            <div className="flex items-center gap-1">
                              {!c.auto_logged && <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-blue-600" onClick={() => { setEditComm(c); setCommDialogOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>}
                              {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteCommId(c.comm_id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                            </div>
                          </div>
                          {c.details && <p className="text-sm text-gray-600 whitespace-pre-wrap mb-1">{c.details}</p>}
                          {c.notes && !c.details && <p className="text-sm text-gray-600 whitespace-pre-wrap mb-1">{c.notes}</p>}
                          {c.linked_records && Object.keys(c.linked_records).length > 0 && (
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span className="text-[10px] text-gray-400 uppercase">Linked:</span>
                              {Object.entries(c.linked_records).map(([key, val]) => (
                                <Badge key={key} data-testid={`linked-record-${key}`} className="rounded-full text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-200">{val}</Badge>
                              ))}
                            </div>
                          )}
                          {c.linked_record_label && !c.linked_records && <p className="text-xs text-gray-400">Linked: {c.linked_record_label}</p>}
                          <p className="text-xs text-gray-400 mt-1">{c.recorded_by_name || c.created_by_name || "System"} - {formatDateLong(c.comm_date)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Edit Customer Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">Edit Customer</DialogTitle></DialogHeader>
          <CustomerForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Communication Dialog */}
      <CommunicationDialog open={commDialogOpen} onOpenChange={setCommDialogOpen} editData={editComm}
        entityType="customer" entityId={customerId} entityName={customer.company_name}
        quotes={quotes} invoices={invoices}
        onSuccess={() => { setCommDialogOpen(false); setEditComm(null);
          api.get("/communications", { params: { entity_type: "customer", entity_id: customerId, limit: 100 } }).then((r) => setComms(r.data.communications || []));
        }} />

      {/* Delete Communication Dialog */}
      <AlertDialog open={!!deleteCommId} onOpenChange={() => setDeleteCommId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Communication?</AlertDialogTitle><AlertDialogDescription>This will permanently remove this communication entry.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteComm} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const COMM_TYPES = ["Email", "Phone Call", "Meeting / In Person", "Quote Discussion", "Invoice Discussion", "Payment Discussion", "Complaint", "General Inquiry", "Other"];

function CommunicationDialog({ open, onOpenChange, editData, entityType, entityId, entityName, quotes, invoices, onSuccess }) {
  const [commType, setCommType] = useState(editData?.comm_type || "Other");
  const [subject, setSubject] = useState(editData?.subject || "");
  const [details, setDetails] = useState(editData?.details || "");
  const [commDate, setCommDate] = useState(editData?.comm_date?.split("T")[0] || new Date().toISOString().split("T")[0]);
  const [linkedType, setLinkedType] = useState(editData?.linked_record_type || "");
  const [linkedId, setLinkedId] = useState(editData?.linked_record_id || "");
  const [followUp, setFollowUp] = useState(editData?.follow_up_required || false);
  const [followUpDate, setFollowUpDate] = useState(editData?.follow_up_date || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editData) {
        setCommType(editData.comm_type || "Other"); setSubject(editData.subject || ""); setDetails(editData.details || "");
        setCommDate(editData.comm_date?.split("T")[0] || new Date().toISOString().split("T")[0]);
        setLinkedType(editData.linked_record_type || ""); setLinkedId(editData.linked_record_id || "");
        setFollowUp(editData.follow_up_required || false); setFollowUpDate(editData.follow_up_date || "");
      } else {
        setCommType("Other"); setSubject(""); setDetails(""); setCommDate(new Date().toISOString().split("T")[0]);
        setLinkedType(""); setLinkedId(""); setFollowUp(false); setFollowUpDate("");
      }
    }
  }, [open, editData]);

  const linkedOptions = linkedType === "quote" ? (quotes || []).map((q) => ({ id: q.quote_id, label: q.quote_number })) :
    linkedType === "invoice" ? (invoices || []).map((i) => ({ id: i.invoice_id, label: i.invoice_number })) : [];

  const handleSave = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setSaving(true);
    const linkedLabel = linkedId ? linkedOptions.find((o) => o.id === linkedId)?.label || "" : "";
    try {
      const payload = { entity_type: entityType, entity_id: entityId, entity_name: entityName, comm_type: commType, subject, details, comm_date: commDate, linked_record_type: linkedType, linked_record_id: linkedId, linked_record_label: linkedLabel, follow_up_required: followUp, follow_up_date: followUp ? followUpDate : "" };
      if (editData) { await api.put(`/communications/${editData.comm_id}`, payload); toast.success("Communication updated"); }
      else { await api.post("/communications", payload); toast.success("Communication logged"); }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">{editData ? "Edit Communication" : "Log Communication"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Date</label>
              <Input type="date" value={commDate} onChange={(e) => setCommDate(e.target.value)} className="rounded-sm border-gray-300 h-10" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Type</label>
              <Select value={commType} onValueChange={setCommType}>
                <SelectTrigger className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{COMM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1">Subject <span className="text-[#E63946]">*</span></label>
            <Input data-testid="comm-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief description" className="rounded-sm border-gray-300 h-10" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1">Details / Notes</label>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="What was discussed..."
              className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Link to Record</label>
              <Select value={linkedType || "none"} onValueChange={(v) => { setLinkedType(v === "none" ? "" : v); setLinkedId(""); }}>
                <SelectTrigger className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="quote">Quote</SelectItem><SelectItem value="invoice">Invoice</SelectItem></SelectContent>
              </Select>
            </div>
            {linkedType && (
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Select {linkedType === "quote" ? "Quote" : "Invoice"}</label>
                <Select value={linkedId || "none"} onValueChange={(v) => setLinkedId(v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{linkedOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} className="rounded border-gray-300" />
            <label className="text-sm text-[#374151]">Follow-up required</label>
            {followUp && <Input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40 ml-auto" />}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="save-communication-button" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? "Saving..." : editData ? "Update" : "Save Communication"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
